import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import CommunitiesHub from "../communities/hub/CommunitiesHub";
import CommunityDashboard from "../communities/CommunityDashboard";
import CommunityPreview from "../communities/CommunityPreview";
import ThreadDetailSection from "../communities/hub/ThreadDetailSection";
import "./PersistentCommunitiesLayout.css";

/**
 * PersistentCommunitiesLayout - Twitter/Reddit Style Navigation
 * 
 * ROOT CAUSE FIX: We ALWAYS render ALL container divs to prevent React's
 * sibling reconciliation from remounting components when conditional 
 * elements appear/disappear.
 * 
 * When you do: {condition && <div>...</div>}
 * And condition changes from true to false, React shifts sibling positions
 * which causes elements to be treated as different and REMOUNT.
 * 
 * Solution: Always render the container divs in FIXED ORDER, hide via CSS,
 * and only conditionally render the CONTENT inside the stable containers.
 */
const PersistentCommunitiesLayout = () => {
  const location = useLocation();
  
  // Parse URL to determine what to show
  const urlInfo = useMemo(() => {
    const path = location.pathname;
    
    // /communities - main hub
    if (path === "/communities" || path === "/communities/") {
      return { view: "hub", communityId: null, threadId: null };
    }
    
    // /communities/:communityId/preview
    const previewMatch = path.match(/^\/communities\/([a-f0-9]{24})\/preview$/i);
    if (previewMatch) {
      return { view: "preview", communityId: previewMatch[1], threadId: null };
    }
    
    // /communities/:communityId/threads/:threadId
    const threadMatch = path.match(/^\/communities\/([a-f0-9]{24})\/threads\/([a-f0-9]{24})$/i);
    if (threadMatch) {
      return { view: "thread", communityId: threadMatch[1], threadId: threadMatch[2] };
    }
    
    // /communities/:communityId - dashboard
    const dashboardMatch = path.match(/^\/communities\/([a-f0-9]{24})$/i);
    if (dashboardMatch) {
      return { view: "dashboard", communityId: dashboardMatch[1], threadId: null };
    }
    
    // Fallback - treat as hub
    return { view: "hub", communityId: null, threadId: null };
  }, [location.pathname]);

  const { view, communityId, threadId } = urlInfo;
  
  // Determine visibility states
  const isHubVisible = view === "hub";
  const isDashboardVisible = view === "dashboard";
  const isThreadVisible = view === "thread";
  const isPreviewVisible = view === "preview";
  
  // Community is "active" when we're inside any community view (dashboard or thread)
  const isCommunityActive = view === "dashboard" || view === "thread";

return (
    <div className="persistent-communities-layout">
      {/* 
        CRITICAL: All 4 container divs are ALWAYS rendered in FIXED ORDER.
        This prevents React sibling reconciliation from remounting.
        Content inside is conditionally rendered, but containers are stable.
      */}
      
      {/* Layer 1: Communities Hub - ALWAYS MOUNTED */}
      <div
        className={`persistent-communities-layout__hub ${
          !isHubVisible ? "persistent-communities-layout__hub--hidden" : ""
        }`}
        aria-hidden={!isHubVisible}
      >
        <CommunitiesHub />
      </div>
      
      {/* Layer 2: Community Dashboard - container ALWAYS rendered for stable siblings */}
      <div
        className={`persistent-communities-layout__dashboard ${
          !isCommunityActive ? "persistent-communities-layout__dashboard--unmounted" : ""
        } ${
          isThreadVisible ? "persistent-communities-layout__dashboard--hidden" : ""
        }`}
        aria-hidden={!isDashboardVisible}
      >
        {/* Only mount CommunityDashboard content when we have a communityId */}
        {isCommunityActive && communityId && (
          <CommunityDashboard key={communityId} communityId={communityId} />
        )}
      </div>
      
      {/* Layer 3: Thread Detail - container ALWAYS rendered for stable siblings */}
      <div
        className={`persistent-communities-layout__thread ${
          !isThreadVisible ? "persistent-communities-layout__thread--hidden" : ""
        }`}
        aria-hidden={!isThreadVisible}
      >
        {isThreadVisible && threadId && <ThreadDetailSection />}
      </div>
      
      {/* Layer 4: Community Preview - container ALWAYS rendered for stable siblings */}
      <div
        className={`persistent-communities-layout__preview ${
          !isPreviewVisible ? "persistent-communities-layout__preview--hidden" : ""
        }`}
        aria-hidden={!isPreviewVisible}
      >
        {isPreviewVisible && communityId && (
          <CommunityPreview communityId={communityId} />
        )}
      </div>
    </div>
  );
};

export default PersistentCommunitiesLayout;
